if (!customElements.get('pb-carousel')) {
  class PageBuilderCarousel extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;

      this.viewport = this.querySelector('[data-pb-carousel-viewport]');
      this.track = this.querySelector('[data-pb-carousel-track]');
      this.prevButton = this.querySelector('[data-pb-carousel-prev]');
      this.nextButton = this.querySelector('[data-pb-carousel-next]');
      this.pagination = this.querySelector('[data-pb-carousel-pagination]');
      if (!this.viewport || !this.track) return;

      this.initialized = true;
      this.mode = this.dataset.mode || 'carousel';
      this.transition = this.dataset.transition || 'slide';
      this.loop = this.dataset.loop === 'true';
      this.autoplay = this.dataset.autoplay === 'true';
      this.pauseOnHover = this.dataset.pauseOnHover === 'true';
      this.scrollBy = this.dataset.scrollBy || 'item';
      this.interval = Math.max(Number(this.dataset.interval) || 5, 1) * 1000;
      this.currentIndex = 0;
      this.originalSlides = Array.from(this.track.children);

      if (this.originalSlides.length === 0) return;

      this.originalSlides.forEach((slide, index) => {
        slide.classList.add('pb-carousel__slide');
        slide.dataset.pbSlideIndex = index;
        slide.setAttribute('role', 'group');
        slide.setAttribute('aria-label', `${index + 1} / ${this.originalSlides.length}`);
      });

      if (this.mode === 'marquee') {
        this.initMarquee();
        return;
      }

      this.bindEvents();
      this.update(true);
      this.resizeObserver = new ResizeObserver(() => this.update(true));
      this.resizeObserver.observe(this.viewport);
      this.blockSelectHandler = (event) => {
        const selectedIndex = this.originalSlides.findIndex((slide) => slide.contains(event.target));
        if (selectedIndex >= 0) {
          this.stopAutoplay();
          this.goTo(selectedIndex, true);
        }
      };
      document.addEventListener('shopify:block:select', this.blockSelectHandler);

      if (this.autoplay && this.originalSlides.length > 1 && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.startAutoplay();
      }
    }

    disconnectedCallback() {
      this.stopAutoplay();
      this.resizeObserver?.disconnect();
      if (this.blockSelectHandler) document.removeEventListener('shopify:block:select', this.blockSelectHandler);
    }

    getPerView() {
      const width = this.getBoundingClientRect().width;
      if (width < 768) return Math.max(Number(this.dataset.perViewMobile) || 1, 1);
      if (width < 1024) return Math.max(Number(this.dataset.perViewTablet) || 2, 1);
      return Math.max(Number(this.dataset.perViewDesktop) || 3, 1);
    }

    getMaxIndex() {
      if (this.transition === 'fade' || this.mode === 'slideshow') {
        return Math.max(this.originalSlides.length - 1, 0);
      }
      return Math.max(this.originalSlides.length - this.getPerView(), 0);
    }

    bindEvents() {
      this.prevButton?.addEventListener('click', () => this.goTo(this.currentIndex - this.getStep()));
      this.nextButton?.addEventListener('click', () => this.goTo(this.currentIndex + this.getStep()));

      if (this.transition !== 'fade') {
        this.viewport.addEventListener('scroll', () => {
          window.clearTimeout(this.scrollTimer);
          this.scrollTimer = window.setTimeout(() => {
            const span = this.getSlideSpan();
            if (span > 0) {
              this.currentIndex = Math.max(0, Math.min(Math.round(this.viewport.scrollLeft / span), this.getMaxIndex()));
              this.updateControls();
            }
          }, 80);
        }, { passive: true });
      }

      if (this.pauseOnHover) {
        this.addEventListener('mouseenter', () => this.stopAutoplay());
        this.addEventListener('mouseleave', () => this.startAutoplay());
        this.addEventListener('focusin', () => this.stopAutoplay());
        this.addEventListener('focusout', () => this.startAutoplay());
      }

      this.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft') this.goTo(this.currentIndex - 1);
        if (event.key === 'ArrowRight') this.goTo(this.currentIndex + 1);
      });
    }

    getStep() {
      return this.scrollBy === 'page' ? this.getPerView() : 1;
    }

    getSlideSpan() {
      const first = this.originalSlides[0];
      const second = this.originalSlides[1];
      if (!first) return 0;
      if (!second) return first.getBoundingClientRect().width;
      return second.offsetLeft - first.offsetLeft;
    }

    goTo(index, immediate = false) {
      const maxIndex = this.getMaxIndex();
      let nextIndex = index;

      if (nextIndex > maxIndex) nextIndex = this.loop ? 0 : maxIndex;
      if (nextIndex < 0) nextIndex = this.loop ? maxIndex : 0;
      this.currentIndex = nextIndex;

      if (this.transition === 'fade') {
        this.originalSlides.forEach((slide, slideIndex) => {
          const active = slideIndex === this.currentIndex;
          slide.classList.toggle('is-active', active);
          slide.setAttribute('aria-hidden', active ? 'false' : 'true');
        });
      } else {
        this.viewport.scrollTo({
          left: this.getSlideSpan() * this.currentIndex,
          behavior: immediate ? 'auto' : 'smooth'
        });
      }

      this.updateControls();
    }

    update(immediate = false) {
      const perView = this.mode === 'slideshow' || this.transition === 'fade' ? 1 : this.getPerView();
      this.style.setProperty('--pb-carousel-per-view', perView);
      this.currentIndex = Math.min(this.currentIndex, this.getMaxIndex());
      this.buildPagination();
      this.goTo(this.currentIndex, immediate);
      this.toggleAttribute('data-inactive', this.originalSlides.length <= perView);
    }

    updateControls() {
      const maxIndex = this.getMaxIndex();
      if (this.prevButton) this.prevButton.disabled = !this.loop && this.currentIndex <= 0;
      if (this.nextButton) this.nextButton.disabled = !this.loop && this.currentIndex >= maxIndex;

      this.pagination?.querySelectorAll('button').forEach((button, index) => {
        const active = index === this.currentIndex;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-current', active ? 'true' : 'false');
      });
    }

    buildPagination() {
      if (!this.pagination) return;

      const pageCount = this.getMaxIndex() + 1;
      this.pagination.replaceChildren();
      for (let index = 0; index < pageCount; index += 1) {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'pb-carousel__dot';
        button.setAttribute('aria-label', `${index + 1} / ${pageCount}`);
        button.addEventListener('click', () => this.goTo(index));
        this.pagination.append(button);
      }
    }

    startAutoplay() {
      if (
        !this.autoplay
        || this.originalSlides.length < 2
        || this.autoplayTimer
        || window.matchMedia('(prefers-reduced-motion: reduce)').matches
      ) return;
      this.autoplayTimer = window.setInterval(() => this.goTo(this.currentIndex + this.getStep()), this.interval);
    }

    stopAutoplay() {
      window.clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }

    initMarquee() {
      if (this.originalSlides.length < 2) {
        this.toggleAttribute('data-inactive', true);
        return;
      }

      const fragment = document.createDocumentFragment();
      this.originalSlides.forEach((slide) => {
        const clone = slide.cloneNode(true);
        clone.classList.add('pb-carousel__slide--clone');
        clone.setAttribute('aria-hidden', 'true');
        clone.querySelectorAll('[id]').forEach((element) => element.removeAttribute('id'));
        clone.querySelectorAll('[data-shopify-editor-block]').forEach((element) => element.removeAttribute('data-shopify-editor-block'));
        clone.querySelectorAll('a, button, input, select, textarea, [tabindex]').forEach((element) => element.setAttribute('tabindex', '-1'));
        fragment.append(clone);
      });
      this.track.append(fragment);

      const updateDistance = () => {
        const firstClone = this.track.querySelector('.pb-carousel__slide--clone');
        if (!firstClone) return;
        this.style.setProperty('--pb-carousel-marquee-distance', `${firstClone.offsetLeft}px`);
      };
      updateDistance();
      this.resizeObserver = new ResizeObserver(updateDistance);
      this.resizeObserver.observe(this.viewport);

      if (this.pauseOnHover) {
        this.addEventListener('mouseenter', () => this.track.style.animationPlayState = 'paused');
        this.addEventListener('mouseleave', () => this.track.style.animationPlayState = 'running');
        this.addEventListener('focusin', () => this.track.style.animationPlayState = 'paused');
        this.addEventListener('focusout', () => this.track.style.animationPlayState = 'running');
      }
    }
  }

  customElements.define('pb-carousel', PageBuilderCarousel);
}
